import type { MediaItem } from "@/data/media";
import type { Project } from "@/data/projects";
import type { Story } from "@/data/stories";

/**
 * The content layer.
 *
 * Everything editable lives in MongoDB, but the components were written against
 * the TypeScript shapes and there was no reason to rewrite twenty-two of them to
 * suit a database. So this module does the translating: it fetches from the API
 * and hands back objects that satisfy the exact same types the components
 * already take, including resolving a story's `mediaKey` back into a whole
 * MediaItem.
 *
 * Two kinds of request, and the split is about what a page carries.
 *
 * The bootstrap holds what the header, the footer and most pages share: the
 * organisation, the menus, the media library, the lists of stories, projects and
 * articles (without their bodies), the impact figures and the forms.
 *
 * A page's own sections arrive with that page, through `fetchPageData`. They
 * used to ride in the bootstrap as well, which meant every page's hydration
 * payload carried every other page's words: the homepage shipped the About
 * page's timeline. Bodies are still excluded too; the detail routes fetch their
 * own document.
 */

/** Server-to-server inside the compose network; a relative path in the browser. */
const API_BASE = typeof window === "undefined" ? (process.env.API_INTERNAL_URL ?? "http://127.0.0.1:4000") : "";

type MediaDoc = {
  key: string;
  asset: string;
  /** "upload" rows are served at their own URL, with the versions made when they arrived. */
  kind?: "asset" | "upload";
  url?: string;
  width?: number;
  height?: number;
  alt: string;
  caption: string;
  project?: string;
  location?: string;
  year?: string;
  credit?: string;
  objectPosition?: string;
  needsConsentCheck?: boolean;
  consentStatus?: "not-required" | "pending" | "granted" | "refused";
  variants?: MediaItem["variants"];
  blurDataUrl?: string;
};

type StoryDoc = Omit<Story, "image" | "body" | "facts"> & {
  mediaKey?: string;
  body?: Story["body"];
  facts?: Story["facts"];
};

type ProjectDoc = Omit<Project, "image" | "gallery"> & {
  mediaKey?: string;
  galleryKeys?: string[];
};

export type ImpactMetricDoc = {
  key: string;
  value: number;
  suffix: "+" | null;
  label: string;
  context: string;
  asOf?: string;
  requiresFinalApproval?: boolean;
  /** Where the figure was published. The Impact page links it so a reader can check. */
  source?: string;
};

export type FaqDoc = { key: string; question: string; answer: string; group: string };

/* ------------------------------------------------------------------ CMS --- */

export type ConfirmableDoc = { value: string; confirmed: boolean; note?: string };

export type LinkDoc = {
  label: string;
  href: string;
  external?: boolean;
  description?: string;
  order?: number;
  hidden?: boolean;
};

export type SettingsDoc = {
  legalName: string;
  shortName: string;
  initials: string;
  establishedYear?: number;
  foundingDate?: string;
  positioning: string;
  motto: string;
  tagline: string;
  mission: string;
  vision: string;
  values: { title: string; body: string; order?: number }[];
  logoMediaKey: string;
  logoDarkMediaKey: string;
  logoReverseMediaKey: string;
  faviconMediaKey: string;
  socialImageUrl: string;
  theme: {
    primary: string;
    secondary: string;
    accent: string;
    headingFont: string;
    bodyFont: string;
    density: "comfortable" | "compact";
  };
  address: ConfirmableDoc;
  addressLocality: string;
  addressRegion: string;
  postalCode: string;
  addressCountry: string;
  phone: ConfirmableDoc;
  email: ConfirmableDoc;
  mapEmbedUrl: ConfirmableDoc;
  safeguardingContact: ConfirmableDoc;
  socialLinks: { label: string; href: string; external?: boolean; order?: number }[];
  registrations: { label: string; value: string; issuer?: string; verified?: boolean; order?: number }[];
  support: {
    donationUrl: string;
    donationEnabled: boolean;
    bankDetailsAvailable: boolean;
    supportContactRoute: string;
    note: string;
  };
  charityTrek: {
    partnerName: string;
    partnerUrl: string;
    destinationUrl: string;
    ctaLabel: string;
    disclosure: string;
  };
};

export type NavGroupDoc = {
  _id?: string;
  location: "primary" | "footer" | "utility";
  heading: string;
  href?: string;
  overviewLabel?: string;
  order: number;
  hidden?: boolean;
  links: LinkDoc[];
};

export type BlockDoc = {
  kind: "heading" | "paragraph" | "list" | "quote" | "image" | "video" | "callout" | "factList";
  text?: string;
  items?: string[];
  attribution?: string;
  mediaKey?: string;
  videoUrl?: string;
  level?: number;
  tone?: "neutral" | "note" | "warning";
  facts?: { label: string; value: string }[];
};

export type SectionItemDoc = {
  title?: string;
  text?: string;
  value?: string;
  label?: string;
  eyebrow?: string;
  mediaKey?: string;
  href?: string;
  external?: boolean;
  icon?: string;
  date?: string;
  source?: string;
  focalPoint?: string;
  facts?: { label: string; value: string; date?: string }[];
  hidden?: boolean;
  order?: number;
};

export type SectionTone = "white" | "mist" | "ivory" | "navy" | "midnight";

export type SectionDoc = {
  _id?: string;
  key?: string;
  type: string;
  order: number;
  hidden?: boolean;
  eyebrow?: string;
  title?: string;
  lead?: string;
  body?: BlockDoc[];
  mediaKey?: string;
  mediaKeys?: string[];
  videoUrl?: string;
  videoTitle?: string;
  videoUploadDate?: string;
  posterMediaKey?: string;
  items?: SectionItemDoc[];
  facts?: { label: string; value: string }[];
  refSlug?: string;
  texts?: Record<string, string>;
  ctas?: LinkDoc[];
  settings?: {
    tone?: SectionTone;
    columns?: number;
    imageSide?: "left" | "right";
    overlayStrength?: number;
    focalPoint?: string;
    align?: "left" | "center";
    compact?: boolean;
  };
};

export type ArticleDoc = {
  slug: string;
  title: string;
  excerpt: string;
  body?: BlockDoc[];
  featuredMediaKey?: string;
  galleryKeys?: string[];
  socialImageKey?: string;
  videoUrl?: string;
  authorName?: string;
  authorRole?: string;
  category: string;
  tags: string[];
  isoDate: string;
  displayDate?: string;
  readingTime?: string;
  reportingPeriod?: string;
  sourceUrl?: string;
  relatedPillarSlug?: string;
  relatedProjectSlug?: string;
  schemaType?: "Article" | "NewsArticle";
  metaTitle?: string;
  metaDescription?: string;
  noindex?: boolean;
  canonicalUrl?: string;
  publishedAt?: string;
  updatedAt?: string;
};

export type GalleryItemDoc = {
  _id?: string;
  kind: "photo" | "video";
  title: string;
  caption: string;
  description?: string;
  mediaKey?: string;
  thumbnailKey?: string;
  videoUrl?: string;
  videoFileUrl?: string;
  durationSeconds?: number;
  programmeSlug?: string;
  projectSlug?: string;
  location?: string;
  year?: string;
  date?: string;
  credit?: string;
  featured?: boolean;
  order: number;
};

/** What a programme card needs. The programme page's own words are its page sections. */
export type ProgrammeDoc = {
  slug: string;
  title?: string;
  shortTitle?: string;
  outcome?: string;
  cta?: string;
  mediaKey?: string;
  cardMediaKey?: string;
  examples?: string[];
  metaTitle?: string;
  metaDescription?: string;
  socialImageKey?: string;
  noindex?: boolean;
  order?: number;
  /* Older API responses carried the page body here too. Read only as a fallback. */
  intro?: string;
  approach?: string[];
  activities?: { title?: string; description?: string; order?: number }[];
  evidence?: { label?: string; value?: string; order?: number }[];
  galleryKeys?: string[];
};

export type PartnerDoc = {
  name: string;
  slug?: string;
  relationship: string;
  summary?: string;
  href?: string;
  logoMediaKey?: string;
  logoUsageApproved?: boolean;
  since?: string;
};

export type DocumentDoc = {
  title: string;
  slug?: string;
  description: string;
  category: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  period?: string;
  publishedDate?: string;
  availability: "download" | "on-request" | "not-published";
};

export type PolicyDoc = {
  slug: string;
  title: string;
  kind: string;
  summary: string;
  body?: BlockDoc[];
  approvalState: "draft" | "awaiting-approval" | "approved";
  approvedBy?: string;
  approvedAt?: string;
  lastReviewedAt?: string;
  /**
   * Never sent to the browser. It is the note staff write to each other about
   * what still needs approving, and it is shown in the admin panel only.
   */
  statusNote?: never;
  contactRoute: string;
  updatedAt?: string;
};

export type FormFieldDoc = {
  name: string;
  label: string;
  type: string;
  help?: string;
  placeholder?: string;
  required?: boolean;
  enabled?: boolean;
  options?: { value: string; label: string }[];
  maxLength?: number;
  order: number;
  locked?: boolean;
};

export type FormDoc = {
  key: string;
  name: string;
  description: string;
  submitLabel: string;
  successNote: string;
  defaultInterest: string;
  fields: FormFieldDoc[];
};

export type PageBannerDoc = {
  mediaKey?: string;
  focalPoint?: string;
  description?: string;
  ctas?: LinkDoc[];
  showBreadcrumb?: boolean;
};

export type PageDoc = {
  _id?: string;
  slug: string;
  title: string;
  headline?: string;
  metaTitle?: string;
  metaDescription?: string;
  eyebrow?: string;
  lead?: string;
  socialImageKey?: string;
  schemaType?: "WebPage" | "AboutPage" | "ContactPage" | "CollectionPage";
  systemRoute?: boolean;
  showInSitemap?: boolean;
  noindex?: boolean;
  canonicalUrl?: string;
  parent?: string;
  routePath?: string;
  banner?: PageBannerDoc;
  updatedAt?: string;
};

/**
 * One page the app renders itself.
 *
 * `seeded` is what tells an unseeded database (render the committed page) from
 * a page whose sections an editor has hidden or removed (render what is saved).
 */
export type PageData = {
  slug: string;
  page: PageDoc | null;
  sections: SectionDoc[];
  seeded: boolean;
  /** The gallery page's rows, which only that page needs. */
  gallery?: GalleryItemDoc[];
};

export type SiteContent = {
  media: Record<string, MediaItem>;
  /** Bodies are not included here. The detail routes load their own document. */
  stories: Story[];
  projects: Project[];
  impact: ImpactMetricDoc[];
  faqs: FaqDoc[];
  settings: SettingsDoc | null;
  navigation: NavGroupDoc[];
  articles: ArticleDoc[];
  partners: PartnerDoc[];
  documents: DocumentDoc[];
  policies: PolicyDoc[];
  forms: FormDoc[];
  programmes: ProgrammeDoc[];
};

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}/api/content${path}`, { signal });
  if (!response.ok) throw new Response(`Content API returned ${response.status}`, { status: 502 });
  return (await response.json()) as T;
}

/**
 * A small cache for the SSR server's page requests.
 *
 * Keyed by path and by the API's content version, which the host reads before
 * every render, so an edit is visible on the next load while a burst of
 * visitors to the same page asks the database once. Server only: in the browser
 * each navigation is a single small request the HTTP cache already handles.
 */
const SERVER_CACHE_MS =
  typeof window === "undefined" && typeof process !== "undefined" && process.env.NODE_ENV === "production" ? 60_000 : 0;
const serverCache = new Map<string, { at: number; version?: string; value: unknown }>();

async function cachedGet<T>(path: string, options: { signal?: AbortSignal; version?: string }): Promise<T> {
  if (SERVER_CACHE_MS > 0) {
    const hit = serverCache.get(path);
    if (hit && hit.version === options.version && Date.now() - hit.at < SERVER_CACHE_MS) return hit.value as T;
  }
  const value = await get<T>(path, options.signal);
  if (SERVER_CACHE_MS > 0) serverCache.set(path, { at: Date.now(), version: options.version, value });
  return value;
}

export const toMediaItem = (d: MediaDoc): MediaItem => ({
  id: d.key,
  asset: d.asset,
  kind: d.kind,
  url: d.url,
  width: d.width,
  height: d.height,
  alt: d.alt,
  caption: d.caption,
  project: d.project,
  location: d.location,
  year: d.year,
  credit: d.credit,
  objectPosition: d.objectPosition,
  needsConsentCheck: d.needsConsentCheck,
  consentStatus: d.consentStatus,
  variants: d.variants,
  blurDataUrl: d.blurDataUrl,
});

/**
 * A missing key is a broken join between a story and its photograph. It gets a
 * placeholder rather than an exception, because one bad reference should not
 * take a whole page down, and the empty alt makes it obvious in the audit.
 */
const MISSING: MediaItem = {
  id: "missing",
  asset: "brand/emblem",
  alt: "",
  caption: "",
};

type BootstrapPayload = {
  media: MediaDoc[];
  stories: StoryDoc[];
  projects: ProjectDoc[];
  impact: ImpactMetricDoc[];
  faqs: FaqDoc[];
  settings: SettingsDoc | null;
  navigation: NavGroupDoc[];
  articles: ArticleDoc[];
  partners: PartnerDoc[];
  documents: DocumentDoc[];
  policies: PolicyDoc[];
  forms: FormDoc[];
  programmes: ProgrammeDoc[];
};

export async function fetchSiteContent(signal?: AbortSignal): Promise<SiteContent> {
  const raw = await get<BootstrapPayload>("/bootstrap", signal);

  const media = Object.fromEntries(raw.media.map((m) => [m.key, toMediaItem(m)]));
  const pick = (key?: string) => (key ? (media[key] ?? MISSING) : MISSING);

  return {
    media,
    stories: raw.stories.map((s) => ({ ...s, image: pick(s.mediaKey), body: s.body ?? [] }) as Story),
    projects: raw.projects.map(
      (p) =>
        ({
          ...p,
          image: pick(p.mediaKey),
          gallery: (p.galleryKeys ?? []).map(pick),
        }) as Project,
    ),
    impact: raw.impact,
    faqs: raw.faqs,
    settings: raw.settings ?? null,
    navigation: raw.navigation ?? [],
    articles: raw.articles ?? [],
    partners: raw.partners ?? [],
    documents: raw.documents ?? [],
    policies: raw.policies ?? [],
    forms: raw.forms ?? [],
    programmes: raw.programmes ?? [],
  };
}

/**
 * One page the app renders itself, with its sections.
 *
 * Never throws. The route exists in code whatever the API says, so a failure
 * here renders the committed page rather than an error; the bootstrap request
 * beside it is what fails loudly when the API is really down.
 */
export async function fetchPageData(
  slug: string,
  options: { signal?: AbortSignal; version?: string } = {},
): Promise<PageData> {
  try {
    const raw = await cachedGet<{ page: PageDoc | null; sections: SectionDoc[]; seeded: boolean }>(
      `/page/${encodeURIComponent(slug)}`,
      options,
    );
    return { slug, page: raw.page ?? null, sections: raw.sections ?? [], seeded: Boolean(raw.seeded) };
  } catch (error) {
    if ((error as Error)?.name === "AbortError") throw error;
    return { slug, page: null, sections: [], seeded: false };
  }
}

/** The gallery's photographs and videos, for /gallery only. */
export async function fetchGallery(options: { signal?: AbortSignal; version?: string } = {}) {
  return await cachedGet<GalleryItemDoc[]>("/gallery", options).catch((error) => {
    if ((error as Error)?.name === "AbortError") throw error;
    return [] as GalleryItemDoc[];
  });
}

/**
 * One story with its body, for /stories/:slug.
 *
 * The API resolves the photograph and sends it inline, so this needs nothing
 * else. That matters on the client: a detail page reached by following a link
 * would otherwise have to pull the whole media table to render one story.
 */
export async function fetchStory(slug: string, signal?: AbortSignal) {
  const doc = await get<(StoryDoc & { media?: MediaDoc | null }) | null>(
    `/stories/${encodeURIComponent(slug)}`,
    signal,
  ).catch(() => null);
  if (!doc) return null;
  return { ...doc, image: doc.media ? toMediaItem(doc.media) : MISSING } as Story;
}

/** One project with its body and gallery, for /projects/:slug. */
export async function fetchProject(slug: string, signal?: AbortSignal) {
  const doc = await get<(ProjectDoc & { media?: MediaDoc | null; galleryMedia?: MediaDoc[] }) | null>(
    `/projects/${encodeURIComponent(slug)}`,
    signal,
  ).catch(() => null);
  if (!doc) return null;
  return {
    ...doc,
    image: doc.media ? toMediaItem(doc.media) : MISSING,
    gallery: (doc.galleryMedia ?? []).map(toMediaItem),
  } as Project;
}

export type ArticleDetail = ArticleDoc & {
  media: MediaItem | null;
  socialMedia: MediaItem | null;
  galleryMedia: MediaItem[];
  related: Pick<ArticleDoc, "slug" | "title" | "excerpt" | "isoDate" | "displayDate" | "category">[];
};

/** One article with its body, featured image and gallery, for /news/:slug. */
export async function fetchArticle(slug: string, signal?: AbortSignal): Promise<ArticleDetail | null> {
  const doc = await get<
    | (ArticleDoc & {
        media?: MediaDoc | null;
        socialMedia?: MediaDoc | null;
        galleryMedia?: MediaDoc[];
        related?: ArticleDoc[];
      })
    | null
  >(`/articles/${encodeURIComponent(slug)}`, signal).catch(() => null);
  if (!doc) return null;
  return {
    ...doc,
    media: doc.media ? toMediaItem(doc.media) : null,
    socialMedia: doc.socialMedia ? toMediaItem(doc.socialMedia) : null,
    galleryMedia: (doc.galleryMedia ?? []).map(toMediaItem),
    related: doc.related ?? [],
  };
}

/** One legal or policy document with its body, for /privacy, /terms and /safeguarding. */
export async function fetchPolicy(slug: string, signal?: AbortSignal): Promise<PolicyDoc | null> {
  return await get<PolicyDoc | null>(`/policies/${encodeURIComponent(slug)}`, signal).catch(() => null);
}

/** A page built entirely in the CMS, with its sections, for /p/:slug. */
export async function fetchPage(slug: string, signal?: AbortSignal) {
  return await get<(PageDoc & { sections: SectionDoc[] }) | null>(
    `/pages/${encodeURIComponent(slug)}`,
    signal,
  ).catch(() => null);
}
